import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CAT_FR = {
  "": "Tout", inscriptions: "Inscriptions", suppressions: "Suppressions", documents: "Documents", vehicules: "Véhicules",
  suspensions: "Suspensions", trajets: "Trajets", paiements: "Paiements", support: "Support / avis", cgu: "CGU", autres: "Autres",
};
const CAT_CLS = {
  inscriptions: "bg-emerald-100 text-emerald-900", suppressions: "bg-red-100 text-red-900", documents: "bg-violet-100 text-violet-900",
  vehicules: "bg-sky-100 text-sky-900", suspensions: "bg-red-200 text-red-900", trajets: "bg-amber-100 text-amber-900",
  paiements: "bg-lime-100 text-lime-900", support: "bg-pink-100 text-pink-900", cgu: "bg-slate-200 text-slate-800", autres: "bg-slate-100 text-slate-700",
};
const PAGE = 50;

// Journal d'activité admin : remplace les e-mails admin (inscriptions, suppressions, documents…).
export default function ActivityLogTab() {
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState(null);

  const load = useCallback(async (skip = 0) => {
    try {
      const { data } = await api.get("/admin/history", { params: { category: cat || undefined, q: q || undefined, skip, limit: PAGE } });
      setItems((prev) => (skip ? [...(prev || []), ...data.items] : data.items));
      setTotal(data.total);
      setErr(null);
    } catch (e) { setErr(formatApiError(e)); }
  }, [cat, q]);

  useEffect(() => { const t = setTimeout(() => load(0), 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.post("/admin/events/seen").catch(() => {}); }, []);

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      <div>
        <p className="text-[12px] font-semibold text-foreground/60">Tout ce qui se passe sur Price : inscriptions, suppressions, documents, véhicules, trajets… Tu reçois aussi une notification sur l&apos;application à chaque événement (plus d&apos;e-mail).</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(CAT_FR).map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setCat(k)}
            className={`rounded-full px-3 py-1.5 text-[12px] font-black street-border-thin ${cat === k ? "bg-primary text-primary-foreground" : "bg-white"}`} data-testid={`activity-filter-${k || "all"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 rounded-xl bg-white px-3 street-border-thin">
        <Search className="h-4 w-4 text-foreground/50" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, e-mail, mot-clé…)" className="w-full bg-transparent py-2 text-[13px] font-semibold outline-none" data-testid="activity-search" />
      </div>
      {err && <p className="text-sm font-bold text-red-700">{err}</p>}
      {!items ? <Loader2 className="h-6 w-6 animate-spin" /> : (
        <>
          <p className="text-[12px] font-bold text-foreground/60" data-testid="activity-total">{total} événement(s)</p>
          {items.length === 0 && <p className="rounded-2xl bg-white p-3 text-[13px] font-semibold street-border-thin" data-testid="activity-empty">Aucun événement.</p>}
          <div className="space-y-1.5">
            {items.map((e) => <EventRow key={e.id} e={e} />)}
          </div>
          {items.length < total && (
            <button type="button" onClick={() => load(items.length)} className="h-10 w-full rounded-full bg-white text-[13px] font-black street-border-thin" data-testid="activity-more">Voir plus</button>
          )}
        </>
      )}
    </div>
  );
}

function EventRow({ e }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" onClick={() => setOpen((o) => !o)} className={`w-full rounded-2xl p-3 text-left street-border-thin ${e.seen ? "bg-white" : "bg-[#FFF1E6]"}`} data-testid={`activity-event-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-black leading-snug">{e.subject}</div>
          <div className="text-[11px] font-semibold text-foreground/50">{new Date(e.created_at).toLocaleString("fr-FR")}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black ${CAT_CLS[e.category] || CAT_CLS.autres}`}>{CAT_FR[e.category] || "Autres"}</span>
      </div>
      {open && e.body && <pre className="mt-2 whitespace-pre-wrap font-[Manrope] text-[12px] font-medium text-foreground/80" data-testid={`activity-event-body-${e.id}`}>{e.body}</pre>}
    </button>
  );
}
